import { useState, useEffect } from "react";
import "../challenge-joining-frames.css";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import Logo from "../../../../../ui/atoms/logo/Logo";
import BackgroundPaths from "../../../../../ui/molecules/background-paths/BackgroundPaths";
import InputText from "../../../../../ui/atoms/inputs/input-text/InputText";
import Button from "../../../../../ui/atoms/buttons/Button";
import {
  onRequestJoiningChallenge,
  onGetRequestJoiningProccessedState,
} from "../../../services/challenge";
import { wait } from "../../../../../core/utils/helpers";
import { challengeSlice } from "../../../../../app/store/slices/challengeSlice";
import { socketIo } from "../../../../../core/socket/socket-client";
import Swal from "sweetalert2";
import useAxiosPrivate from "../../../../../app/providers/context-provider/useAxiosPrivate";

const JoiningMainFrame = () => {
  const axiosPrivate = useAxiosPrivate();

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [isSending, setIsSending] = useState(false);
  const [challengeCode, setChallengeCode] = useState("");
  const [challengeData, setChallengeData] = useState({});
  const [isWaitingForApproval, setIsWaitingForApproval] = useState(null);

  const initConnectedUser = useSelector(
    (state) => state.setInitConf.initConnectedUser,
  );
  const _sys_role = initConnectedUser?.sys_role;

  const currentConnectedStream = useSelector(
    (state) => state.setInitConf.currentConnectedStream,
  );
  const current_stream_id = currentConnectedStream?.stream_id;
  const _inscription_id = _sys_role == "student" ? currentConnectedStream?.inscription_id : initConnectedUser?.inscription;


  useEffect(() => {
    if (isWaitingForApproval) {
      Swal.fire({
        icon: "info",
        title: "Request Joining Challenge",
        text: "⏳ Your request to join the challenge is waiting for approval.",
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });
      return;
    }
  }, [isWaitingForApproval]);

  useEffect(() => {
    // Authenticate host to socket server in order to receive real-time events
    socketIo.emit("authenticate", { inscription_id: _inscription_id });

    const onHandleRequestState = (data) => {
      if (data?.status) onRefreshRequestState();
    };

    socketIo.on(
      "on_request_joining_challenge_process_state",
      onHandleRequestState,
    );

    // clean up function when component unmounts
    return () => {
      socketIo.off(
        "on_request_joining_challenge_process_state",
        onHandleRequestState,
      );
    };
  }, [challengeData]);

  const onHandleRequestJoiningChallenge = async () => {
    setIsSending(true);
    await wait(500);
    //
    const localData = {
      inscription_id: _inscription_id,
      challenge_code: challengeCode,
      request_dates: new Date(),
    };

    onRequestJoiningChallenge(axiosPrivate, localData)
      .then((response) => {
        const _data = response?.data;
        const _status = _data?.status;
        if (_status) {
          const _data_challenge = _data?.result?.challenge;
          const _request_id = _data?.result?.request_id;
          const is_waiting_for_approval = _data?.is_waiting_for_approval;
          dispatch(
            challengeSlice?.actions?.setCurrentAssessment({
              mode: "CH", // CH stand for CHALLENGE
              challenge_id: _data_challenge?.id,
              challenge_type: _data_challenge?.type,
              teams_breakdown: _data_challenge?.teams_breakdown,
              teams_device: _data_challenge?.teams_device,
              request_id: _request_id,
              admission_test_id: null,
            }),
          );
          setIsWaitingForApproval(is_waiting_for_approval);
          setChallengeData(_data_challenge);
          if (!is_waiting_for_approval) {
            const _allow_nickname = _data_challenge?.allowing_nickname;
            if (_allow_nickname) {
              const _to = `/${_sys_role}/challenge/joining/setup/nickname`;
              navigate(_to, { replace: true });
            } else {
              const _challenge_type = _data_challenge?.type;
              if (_challenge_type === "classic") {
                const _to = `/${_sys_role}/challenge/joining/waiting/room`;
                navigate(_to, { replace: true });
              } else {
                const _teams_breakdown = _data_challenge?.teams_breakdown;
                if (_teams_breakdown === "self-choice") {
                  const _to = `/${_sys_role}/challenge/joining/team/choice`;
                  navigate(_to, { replace: true });
                }
                if (_teams_breakdown === "self-compose") {
                  const _to = `/${_sys_role}/challenge/joining/team/compose`;
                  navigate(_to, { replace: true });
                }
              }
            }
          } else {
            Swal.fire({
              icon: "info",
              title: "Request Joining Challenge",
              text: _data?.message,
            });
          }
        }
      })
      .catch((error) => {
        setIsSending(false);
        if (!error?.response) {
          Swal.fire({
            icon: "error",
            title: "Error",
            text: "No server response",
          });
        } else {
          Swal.fire({
            icon: "warning",
            title: "Warning",
            text: error.response.data?.message || "An error occurred",
          });
        }
      });
  };

  const onRefreshRequestState = async () => {
    let isMounted = true;
    const controller = new AbortController();
    const signal = controller.signal;

    const challengeId = challengeData?.id;
    const localData = {
      inscription_id: _inscription_id,
      challenge_id: challengeId,
    };

    onGetRequestJoiningProccessedState(axiosPrivate, localData, signal)
      .then((response) => {
        const _data = response?.data?.result;
        const is_authorized = _data?.is_authorized;
        const request_id = _data?.request_id;
        //
        Swal.close();
        //
        if (is_authorized) {
          dispatch(
            challengeSlice?.actions?.setCurrentAssessment({
              mode: "CH", // CH stand for CHALLENGE
              challenge_id: challengeData?.id,
              challenge_type: challengeData?.type,
              teams_breakdown: challengeData?.teams_breakdown,
              teams_device: challengeData?.teams_device,
              request_id: request_id,
              admission_test_id: null,
            }),
          );
          const _allow_nickname = challengeData?.allowing_nickname;
          if (_allow_nickname) {
            const _to = `/${_sys_role}/challenge/joining/setup/nickname`;
            navigate(_to, { replace: true });
          } else {
            const _challenge_type = challengeData?.type;
            if (_challenge_type === "classic") {
              const _to = `/${_sys_role}/challenge/joining/waiting/room`;
              navigate(_to, { replace: true });
            } else {
              const _teams_breakdown = challengeData?.teams_breakdown;
              if (_teams_breakdown === "self-choice") {
                const _to = `/${_sys_role}/challenge/joining/team/choice`;
                navigate(_to, { replace: true });
              }
              if (_teams_breakdown === "self-compose") {
                const _to = `/${_sys_role}/challenge/joining/team/compose`;
                navigate(_to, { replace: true });
              }
            }
          }
        } else {
          Swal.fire({
            icon: "warning",
            title: "Request Denied",
            text: "❌ Sorry! Your were not authorize to join the challenge by the host.",
          });
        }
        //
      })
      .catch((error) => {
        if (!error?.response) {
          Swal.fire({
            icon: "error",
            title: "Error",
            text: "No server response",
          });
        } else {
          Swal.fire({
            icon: "warning",
            title: "Warning",
            text: error?.response?.data?.message,
          });
        }
      });
    return () => {
      isMounted = false;
      isMounted && controller.abort();
    };
  };

  return (
    <div className="joining-frame">
      <div className="j-f-inner">
        <BackgroundPaths />
      </div>
      <div className="j-f-outer">
        <div className="j-f-outer-head">
          <Logo to={null} width={"200px"} />
          <h1>Pamoja 4 Machine Intelligence University</h1>
          <p>Joining a Challenge.!</p>
        </div>
        <div className="j-f-outer-wrapper w-50">
          <div className="w-full d-flex justify-center align-center gap-md p-lg">
            <div className="w-60">
              <InputText
                name="challenge_code"
                title="Type the Challenge's Code Here..."
                value={challengeCode}
                onChange={(e) => setChallengeCode(e.target.value)}
                disabled={isSending ? true : false}
              />
            </div>
            {challengeCode.length === 9 &&
              (isSending ? (
                <span style={{ color: "red" }}>Requesting...</span>
              ) : (
                <div className="fade-in">
                  <Button
                    type={"button"}
                    preferedStyle={"normal"}
                    title={"Request joining, Go!"}
                    action={() => onHandleRequestJoiningChallenge()}
                  />
                </div>
              ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default JoiningMainFrame;
